import { useEffect, useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays } from "lucide-react";
import { fetchSlots } from "@/lib/api";
import SlotsView from "@/components/booking/SlotsView";

const fmtDate = (d) => {
  // Local YYYY-MM-DD (avoid timezone drift from toISOString)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function Step2DateTime({
  serviceIds,
  availability,
  selectedDate,
  selectedTime,
  onSelectDate,
  onSelectTime,
}) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  // Build a set of closed weekdays from availability (0=Mon..6=Sun)
  const closedWeekdays = useMemo(() => {
    const set = new Set();
    availability.forEach((a) => { if (a.is_closed) set.add(a.day_of_week); });
    return set;
  }, [availability]);

  // react-day-picker uses Sunday=0..Saturday=6, but our backend uses Mon=0..Sun=6.
  // Convert: jsDay(getDay()) -> backendDay = (jsDay + 6) % 7
  const isDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    const backendDay = (date.getDay() + 6) % 7;
    return closedWeekdays.has(backendDay);
  };

  useEffect(() => {
    if (!selectedDate || serviceIds.length === 0) {
      setSlots([]);
      setInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setInfo(null);
      try {
        const data = await fetchSlots(fmtDate(selectedDate), serviceIds);
        if (cancelled) return;
        setSlots(data.slots || []);
        if (data.is_closed) setInfo("This day is closed.");
        else if ((data.slots || []).length === 0) setInfo("No available time slots for this date. Please pick another day.");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not load time slots.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Re-fetch only when the selected date or chosen services change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, serviceIds]);

  return (
    <div className="pp-step-fade" data-testid="step-2-datetime">
      <h2 className="text-xl md:text-2xl font-medium mb-1">Pick a date & time</h2>
      <p className="text-xs sm:text-sm mb-3" style={{ color: "var(--pp-muted)" }}>
        Past dates and closed days are unavailable.
      </p>

      <div className="flex flex-col md:flex-row gap-3 md:gap-5">
        <div
          className="rounded-[6px] flex justify-center px-1 py-1 md:flex-shrink-0"
          style={{ background: "#fff", border: "1px solid var(--pp-border)" }}
          data-testid="calendar-container"
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => {
              onSelectDate(d || null);
              onSelectTime(null);
            }}
            disabled={isDisabled}
            initialFocus
            data-testid="booking-calendar"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-4 h-4" style={{ color: "var(--pp-indigo)" }} />
            <h3 className="text-sm font-semibold">
              {selectedDate
                ? selectedDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
                : "Select a date"}
            </h3>
          </div>

          <SlotsView
            loading={loading}
            error={error}
            slots={slots}
            info={info}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onSelectTime={onSelectTime}
          />
        </div>
      </div>
    </div>
  );
}
